import i18next from "i18next";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { APPS, LABS } from "../../constants/AppsConstants.ts";
import { POSTHOG_EVENTS } from "../../constants/PostHogEvents";
import { IMAGE_REFERENCE_CONFIGS } from "../../constants/TextToImageConstants.ts";
import { BoundingBox } from "../../layout/LabsLayout/Results/Canvas/types";
import { IRootStore } from "../../mobx/root-store.tsx";
import { PlaygroundImage, PlaygroundResult } from "../../models/image-to-image.ts";
import { TextToImageConfigType } from "../../models/text-to-image.ts";
import postHogService from "../../services/PostHogService";
import { isFibo2App, isFiboApp, isFiboBbqApp } from "../../utils";
import QueryService from "../../utils/QueryService.ts";
import { FiboEditConfigType, FiboEditStore, IFiboEditStore } from "../FiboEdit/fibo-edit-store.tsx";
import { IFiboResultGroup } from "../Playground/store/playground-store.tsx";
import StructuredPromptStore, { IStructuredPromptStore } from "./StructuredPrompt/structured-prompt-store.tsx";

export type FiboConfigType = TextToImageConfigType | FiboEditConfigType;

export type FiboActiveTab = "generate" | "refine";

export interface GenerateTextToImageV2Object {
	textToImageConfig?: TextToImageConfigType;
	isRefine?: boolean;
	isTgEvaluation?: boolean;
	skipGenerateStructuredPrompt?: boolean;
	image?: PlaygroundImage;
	type?: APPS | LABS;
}

interface GenerationContext {
	config: TextToImageConfigType;
	isRefine: boolean | undefined;
	isTgEvaluation: boolean | undefined;
	skipGenerateStructuredPrompt: boolean | undefined;
	image: PlaygroundImage | undefined;
	isTg: boolean;
	modelName: string;
	savedIndex: number;
	numResults: number;
	resultsSkeletons: PlaygroundResult;
	capturedImageReferenceUrl: string | undefined;
	capturedImageReferenceFile: File | undefined;
}

interface ImageGenerationParams {
	config: TextToImageConfigType;
	structured_prompt: string | undefined;
	seed: number | undefined;
	image: PlaygroundImage | undefined;
	isRefine: boolean | undefined;
	skipGenerateStructuredPrompt: boolean | undefined;
	modelName: string;
	numResults: number;
	capturedImageReferenceFile: File | undefined;
}

interface SingleImageGenerationParams {
	imageIndex: number;
	payload: any;
	config: TextToImageConfigType;
	isRefine: boolean | undefined;
	isTg: boolean;
	isTgEvaluation: boolean | undefined;
	savedIndex: number;
	seed: number | undefined;
	skipGenerateStructuredPrompt: boolean | undefined;
	image: PlaygroundImage | undefined;
	randomSeed: number;
	refImageUrl: any;
	capturedImageReferenceFile: File | undefined;
}

interface ImageGenerationPayload {
	payload: any;
	refImageUrl: any;
	randomSeed: number;
}

export interface IFiboStore {
	activeTab: FiboActiveTab;
	fiboEditStore: IFiboEditStore;
	structuredPromptStore: IStructuredPromptStore;

	// JSON editor state (FIBO 2)
	inputMode: "form" | "json";
	jsonInput: string;

	// Generation state
	abortController: AbortController | null;
	isGeneratingStructuredPrompt: boolean;
	isPollingStatus: boolean;
	lastGeneratedResultId: string | null;

	setActiveTab: (tab: FiboActiveTab) => void;
	setInputMode: (mode: "form" | "json") => void;
	setJsonInput: (value: string) => void;
	updateFiboResultGroup: (resultId: string, updates: Partial<IFiboResultGroup>) => void;
	getSelectedFiboGroup: () => IFiboResultGroup | undefined;
	isSelectedGroupStructuredPromptModified: () => boolean;

	// Generation methods
	generate: (generateTextToImageV2Object?: GenerateTextToImageV2Object) => Promise<void>;
	refine: (functionOptions?: Omit<GenerateTextToImageV2Object, "isRefine">) => Promise<void>;
	generateStructuredPrompt: (
		textToImageConfig?: TextToImageConfigType,
		isRefine?: boolean,
		refineNonFiboImage?: boolean,
		image?: PlaygroundImage,
	) => Promise<any>;
	handleStructuredPromptChange: (structuredPrompt: string) => void;
	handleStructuredPromptDiffChange: (structuredPromptDiff: string | undefined) => void;
	abortImageGeneration: () => void;
	setPollingStatus: (isPolling: boolean) => void;
	setLastGeneratedResultId: (resultId: string) => void;
	registerVglFlush: (fn: () => void) => () => void;
	flushVglEdits: () => void;
}

/**
 * FIBO 2 VGL contract (Dawoud + Lev, 2026-08-24): the vgl bundled inline in an
 * /image_generate_fibo2 (or /embed_object_fibo2) response is experimental model
 * output and is NEVER used — not shown, not adopted, not compared against. The VGL
 * Output is only ever a /generate_vgl_fibo2 caption, a /refine_vgl_fibo2 result, or
 * the user's own edits.
 */
export default class FiboStore implements IFiboStore {
	private rootStore: IRootStore;
	fiboEditStore: IFiboEditStore;
	structuredPromptStore: IStructuredPromptStore;

	activeTab: FiboActiveTab = "generate";

	inputMode: "form" | "json" = "form";
	jsonInput: string = "{}";

	private imageGenerationsQueryService: QueryService = new QueryService("/image_generations");
	private fileQueryService: QueryService = new QueryService("/upload-image");
	private pollingIntervalId: number | null = null;

	// Generation state
	abortController: AbortController = new AbortController();
	isGeneratingStructuredPrompt: boolean = false;
	isPollingStatus: boolean = false;
	lastGeneratedResultId: string | null = null;

	private vglFlushers: Set<() => void> = new Set();

	private isApplyingJsonToForm: boolean = false;

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
		this.fiboEditStore = new FiboEditStore(rootStore);
		this.structuredPromptStore = new StructuredPromptStore();

		reaction(
			() => {
				const c = (this.rootStore.textToImageStore?.config ?? {}) as TextToImageConfigType;
				return JSON.stringify([
					this.activeTab,
					c.prompt,
					c.aspect_ratio,
					c.steps_num,
					c.text_guidance_scale,
					c.negative_prompt,
					c.seed,
				]);
			},
			() => {
				if (!isFibo2App() || this.isApplyingJsonToForm) return;
				this.syncJsonFromForm();
			},
		);
	}

	setActiveTab = (tab: FiboActiveTab) => {
		this.activeTab = tab;
	};

	setInputMode = (mode: "form" | "json") => {
		runInAction(() => {
			this.inputMode = mode;
		});
		if (mode === "json") this.syncJsonFromForm();
	};

	setJsonInput = (value: string) => {
		runInAction(() => {
			this.jsonInput = value;
		});
		this.applyJsonToForm(value);
	};

	private buildVglPlaceholder = () => ({
		general: { image_type: "", style: "", description: "", color_palette: [] },
		foreground: [],
		midground: [],
		background: [],
	});

	private isEmptyVgl = (vgl: any): boolean => {
		if (!vgl || typeof vgl !== "object") return true;
		const g = vgl.general ?? {};
		const generalEmpty =
			!g.image_type &&
			!g.style &&
			!g.description &&
			(!Array.isArray(g.color_palette) || g.color_palette.length === 0);
		const layersEmpty = ["foreground", "midground", "background"].every(
			(k) => !Array.isArray(vgl[k]) || vgl[k].length === 0,
		);
		return generalEmpty && layersEmpty;
	};

	private syncJsonFromForm = () => {
		const c = (this.rootStore.textToImageStore?.config ?? {}) as TextToImageConfigType;

		let existing: any = {};
		try {
			existing = this.jsonInput ? JSON.parse(this.jsonInput) : {};
		} catch {
			existing = {};
		}
		const vgl =
			existing && typeof existing === "object" && existing.vgl ? existing.vgl : this.buildVglPlaceholder();

		const next =
			this.activeTab === "refine"
				? { edit_instruction: c.prompt ?? "", vgl }
				: {
						prompt: c.prompt ?? "",
						aspect_ratio: c.aspect_ratio ?? "1:1",
						steps_num: c.steps_num ?? 50,
						guidance_scale: c.text_guidance_scale ?? 5,
						negative_prompt: c.negative_prompt ?? "",
						seed: c.seed ?? null,
						vgl,
					};

		runInAction(() => {
			this.jsonInput = JSON.stringify(next, null, 2);
		});
	};

	private applyJsonToForm = (value: string) => {
		let parsed: any;
		try {
			parsed = JSON.parse(value);
		} catch {
			return;
		}
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return;

		const tts = this.rootStore.textToImageStore;
		if (!tts) return;

		this.isApplyingJsonToForm = true;
		try {
			runInAction(() => {
				if (this.activeTab === "refine") {
					if ("edit_instruction" in parsed) tts.handleConfigChange("prompt", parsed.edit_instruction ?? "");
				} else {
					if ("prompt" in parsed) tts.handleConfigChange("prompt", parsed.prompt ?? "");
					if ("aspect_ratio" in parsed) tts.handleConfigChange("aspect_ratio", parsed.aspect_ratio);
					if ("steps_num" in parsed) tts.handleConfigChange("steps_num", parsed.steps_num);
					if ("guidance_scale" in parsed)
						tts.handleConfigChange("text_guidance_scale", parsed.guidance_scale);
					if ("negative_prompt" in parsed) tts.handleConfigChange("negative_prompt", parsed.negative_prompt);
					if ("seed" in parsed) tts.handleConfigChange("seed", parsed.seed ?? undefined);
				}
			});
		} finally {
			this.isApplyingJsonToForm = false;
		}
	};

	/**
	 * Updates the config of the fibo result (playground result)
	 *
	 * FYI: Updates the non-computed properties of the config!
	 *
	 * @param resultId - The id of the playgroundResult to update
	 * @param updates - The updates to apply to the result
	 */
	updateFiboResultGroup = (resultId: string, updates: Partial<IFiboResultGroup>) => {
		runInAction(() => {
			const result = this.rootStore.playgroundStore.playgroundResults.find((r) => r.id === resultId);
			if (!result) return;

			const cfg = result.config as any;
			const isFiboResult =
				cfg?.structured_prompt !== undefined ||
				cfg?.is_refine !== undefined ||
				result.type === LABS.FIBO ||
				result.type === LABS.FIBO_EDIT;

			if (!isFiboResult) return;

			// If structured_prompt is being updated and original_structured_prompt doesn't exist,
			// preserve the current structured_prompt as the original before updating
			// This ensures we can track modifications even if original wasn't set initially
			if (updates.structured_prompt !== undefined && !cfg.original_structured_prompt && cfg.structured_prompt) {
				cfg.original_structured_prompt = cfg.structured_prompt;
			}

			// Update config properties (excluding computed properties: loading, is_structured_prompt_loading, selectedImage)
			const configProps = [
				"structured_prompt",
				"original_structured_prompt",
				"structured_prompt_diff",
				"delta_prompt",
				"seed",
				"is_refine",
			] as const;
			configProps.forEach((prop) => {
				if (updates[prop] !== undefined) {
					cfg[prop] = updates[prop];
				}
			});

			// Handle selectedImage - store the selected image ID in config
			if (updates.selectedImage !== undefined) {
				cfg.selectedImageId = "id" in updates.selectedImage ? updates.selectedImage.id : undefined;
			}
		});
	};

	getSelectedFiboGroup = (): IFiboResultGroup | undefined => {
		const { playgroundStore } = this.rootStore;
		const selectedResult = playgroundStore.selectedResult;
		if (!selectedResult) return undefined;

		const group = playgroundStore.labResultGroups.find((group) => group.id === selectedResult.id);
		return playgroundStore.isFiboResultGroup(group) ? group : undefined;
	};

	isSelectedGroupStructuredPromptModified = (): boolean => {
		const selectedGroup = this.getSelectedFiboGroup();
		if (!selectedGroup) return false;
		if (!selectedGroup.original_structured_prompt || !selectedGroup.structured_prompt) return false;

		try {
			const original = JSON.stringify(JSON.parse(selectedGroup.original_structured_prompt));
			const current = JSON.stringify(JSON.parse(selectedGroup.structured_prompt));
			return original !== current;
		} catch {
			return selectedGroup.original_structured_prompt !== selectedGroup.structured_prompt;
		}
	};

	// Method to track status polling state
	setPollingStatus = (isPolling: boolean): void => {
		runInAction(() => {
			this.isPollingStatus = isPolling;
		});
	};

	setLastGeneratedResultId = (resultId: string): void => {
		runInAction(() => {
			this.lastGeneratedResultId = resultId;
		});
	};

	/**
	 * Register a flush callback from a VGL editor (canvas bbox/color debounce, tree edit
	 * debounce). Returns an unregister function. `flushVglEdits()` runs all of them so a
	 * just-made edit is committed to the group before generate/refine reads the vgl.
	 */
	registerVglFlush = (fn: () => void): (() => void) => {
		this.vglFlushers.add(fn);
		return () => {
			this.vglFlushers.delete(fn);
		};
	};

	flushVglEdits = (): void => {
		this.vglFlushers.forEach((fn) => {
			try {
				fn();
			} catch (e) {
				console.error("Failed to flush a pending VGL edit:", e);
			}
		});
	};

	abortImageGeneration = (): void => {
		const { playgroundStore, textToImageStore } = this.rootStore;
		if (this.abortController) {
			this.abortController.abort();
			// Create a new AbortController for future requests
			this.abortController = new AbortController();
		}

		if (this.pollingIntervalId !== null) {
			clearInterval(this.pollingIntervalId);
			this.pollingIntervalId = null;
		}

		playgroundStore.removeNotLoadingImages();

		// Jump to the result that was being generated and display its structured prompt
		const lastResult = this.lastGeneratedResultId
			? playgroundStore.playgroundResults.find((r) => r.id === this.lastGeneratedResultId)
			: null;

		if (lastResult) {
			playgroundStore.selectedResult = lastResult;

			// Get the Fibo group for this result
			const group = playgroundStore.labResultGroups.find((g) => g.id === lastResult.id);
			if (playgroundStore.isFiboResultGroup(group)) {
				// Apply the structured prompt and diff from the group
				const effectiveStructuredPrompt = group.structured_prompt;
				const effectiveDiff = group.structured_prompt_diff;

				if (effectiveStructuredPrompt) {
					this.handleStructuredPromptChange(effectiveStructuredPrompt);
				}
				if (effectiveDiff) {
					this.handleStructuredPromptDiffChange(effectiveDiff);
				}
			}
		}

		runInAction(() => {
			textToImageStore.IpSignalWarning = false;
			textToImageStore.isGeneratingImages = false;
			this.isGeneratingStructuredPrompt = false;
			this.isPollingStatus = false;
		});
	};

	handleStructuredPromptChange = (structuredPrompt: string): void => {
		const selectedGroup = this.getSelectedFiboGroup();
		if (selectedGroup) {
			this.updateFiboResultGroup(selectedGroup.id, {
				structured_prompt: structuredPrompt,
			});
		}
	};

	handleStructuredPromptDiffChange = (structuredPromptDiff: string | undefined): void => {
		const selectedGroup = this.getSelectedFiboGroup();
		if (selectedGroup) {
			this.updateFiboResultGroup(selectedGroup.id, {
				structured_prompt_diff: structuredPromptDiff,
			});
		}
	};

	/**
	 * Detects if an error is a content moderation error.
	 * Returns information about the type of content moderation error if detected.
	 *
	 * @returns Object with isContentModeration flag and error details, or null if not a content moderation error
	 */
	private detectContentModerationError = (
		error: any,
	): {
		isContentModeration: boolean;
		isPromptContentModeration: boolean;
		isImageReferenceContentModeration: boolean;
		status: number;
	} | null => {
		const errorMessage = error?.response?.data?.error?.message || error?.response?.data?.message || "";
		const errorDetails = error?.response?.data?.error?.details || "";
		const status = error?.response?.status || 422;

		const isPromptContentModeration =
			errorMessage.toLowerCase().includes("prompt did not pass content moderation") ||
			errorDetails.toLowerCase().includes("prompt did not pass content moderation");
		const isImageReferenceContentModeration =
			errorMessage.toLowerCase().includes("visual in the images parameter did not pass content moderation") ||
			errorDetails.toLowerCase().includes("visual in the images parameter did not pass content moderation");
		const isGenericContentModeration =
			!isPromptContentModeration &&
			!isImageReferenceContentModeration &&
			(errorMessage.toLowerCase().includes("content moderation") ||
				errorDetails.toLowerCase().includes("content moderation"));

		if (isPromptContentModeration || isImageReferenceContentModeration || isGenericContentModeration) {
			return {
				isContentModeration: true,
				isPromptContentModeration,
				isImageReferenceContentModeration,
				status,
			};
		}

		return null;
	};

	/**
	 * Handles content moderation errors from API responses.
	 * Checks for prompt and image reference content moderation errors and sets appropriate error messages.
	 *
	 * @param err - The error object from the API response
	 * @param options.handleImageReferenceError - Whether to handle image reference content moderation errors specifically (default: true)
	 * @param options.promptErrorMessage - Custom error message for prompt content moderation (default: `"promptContentModeration"`)
	 */
	private handleContentModerationError = (
		err: any,
		options: {
			handleImageReferenceError?: boolean;
			promptErrorMessage?: string;
		} = {},
	): void => {
		const { textToImageStore } = this.rootStore;
		const { handleImageReferenceError = true, promptErrorMessage = "promptContentModeration" } = options;

		const moderationInfo = this.detectContentModerationError(err);

		if (moderationInfo?.isImageReferenceContentModeration && handleImageReferenceError) {
			textToImageStore.imageReferenceContentModerationError = true;
			textToImageStore.errors.generate = {
				message: "imageReferenceContentModeration",
				status: moderationInfo.status || 422,
			};
		} else if (moderationInfo?.isPromptContentModeration) {
			textToImageStore.errors.generate = {
				message: promptErrorMessage,
				status: moderationInfo.status || 422,
			};
		} else if (moderationInfo?.isContentModeration) {
			textToImageStore.errors.generate = {
				message: "Content moderation error",
				status: moderationInfo.status || 422,
			};
		} else {
			textToImageStore.errors.generate = {
				message: err.response?.data?.message || err.message || "Image generation failed",
				status: err.response?.status || 500,
			};
		}
	};

	private buildStructuredPromptPayload = (
		config: TextToImageConfigType,
		isRefine: boolean,
		refineNonFiboImage: boolean,
		image?: PlaygroundImage,
	): any => {
		const payload: any = {
			sync: true,
			ip_signal: true,
		};

		if (["fibo", "fibo_lite"].includes(config.model as string)) {
			payload.model_version = isFiboBbqApp() ? "FIBO_BBQ" : isFibo2App() ? "FIBO_2" : config.model_version;
		}

		if (isRefine && refineNonFiboImage) {
			payload.model = config.model;
			payload.num_steps = 50;
			payload.seed = image?.seed;
			payload.images = [image?.url];
		}

		const { textToImageStore } = this.rootStore;
		if (config.prompt && !refineNonFiboImage && !(isFiboBbqApp() && textToImageStore.imageReference?.file)) {
			payload.prompt = config.prompt;
		}

		if (config.seed !== undefined && config.seed !== null) {
			payload.seed = config.seed;
		}

		return payload;
	};

	/**
	 * Adds refine-specific structured prompt fields to the payload
	 */
	private addRefineStructuredPromptToPayload = (
		payload: any,
	): { payload: any; isStructuredPromptModified: boolean } => {
		const selectedGroup = this.getSelectedFiboGroup();
		const isStructuredPromptModified = Boolean(selectedGroup && this.isSelectedGroupStructuredPromptModified());

		if (selectedGroup) {
			if (isStructuredPromptModified) {
				// Send original as structured_prompt
				if (selectedGroup.original_structured_prompt) {
					payload.structured_prompt = selectedGroup.original_structured_prompt;
				}
				// Send modified as user_adjusted_structured_prompt
				if (selectedGroup.structured_prompt) {
					payload.user_adjusted_structured_prompt = selectedGroup.structured_prompt;
				}
			} else {
				if (selectedGroup.structured_prompt) {
					payload.structured_prompt = selectedGroup.structured_prompt;
				}
			}
		}

		return { payload, isStructuredPromptModified };
	};

	private makeStructuredPromptApiCall = async (
		endpoint: string,
		payload: any,
		_config: TextToImageConfigType,
		_isStructuredPromptModified: boolean,
		image?: PlaygroundImage,
		isRefine?: boolean,
	): Promise<any> => {
		const { textToImageStore } = this.rootStore;

		if (textToImageStore.imageReference?.file && !(image?.refine_prompt && isRefine)) {
			const formData = new FormData();
			formData.append("file", textToImageStore.imageReference.file);
			formData.append(
				"config",
				JSON.stringify({
					...payload,
					visual_input_content_moderation: true,
				}),
			);

			return await this.imageGenerationsQueryService.post(
				endpoint,
				formData,
				{ "Content-Type": "multipart/form-data" },
				{ signal: this.abortController.signal },
			);
		} else {
			return await this.imageGenerationsQueryService.post(
				endpoint,
				payload,
				{ "Content-Type": "application/json" },
				{ signal: this.abortController.signal },
			);
		}
	};

	/**
	 * Extracts the structured prompt result and handles IP warnings.
	 * When refining, saves the current structured_prompt as structured_prompt_diff before updating.
	 * Updates the selected group's structured_prompt so it's available for UI display.
	 */
	private getStructuredPromptResult = (structuredPromptResponse: any) =>
		structuredPromptResponse?.result ?? structuredPromptResponse;

	private updateStoreWithStructuredPromptResponse = (structuredPromptResponse: any, isRefine: boolean): any => {
		const { textToImageStore } = this.rootStore;
		const structuredPromptResult = this.getStructuredPromptResult(structuredPromptResponse);

		runInAction(() => {
			this.isGeneratingStructuredPrompt = false;

			const selectedGroup = this.getSelectedFiboGroup();
			const newStructuredPrompt = structuredPromptResult.structured_prompt;
			const deltaPrompt = structuredPromptResult.delta_prompt;

			// When refining, save the current structured_prompt as diff before it gets updated
			if (isRefine && selectedGroup?.structured_prompt) {
				this.updateFiboResultGroup(selectedGroup.id, {
					structured_prompt_diff: selectedGroup.structured_prompt,
				});
			}

			// Update the selected group's structured_prompt so it's immediately available for UI display
			if (selectedGroup && newStructuredPrompt) {
				const updates: any = { structured_prompt: newStructuredPrompt };
				deltaPrompt && (updates.delta_prompt = deltaPrompt);
				isRefine && (updates.original_structured_prompt = newStructuredPrompt);

				this.updateFiboResultGroup(selectedGroup.id, updates);
			}

			// Check for IP warning in the response
			if (structuredPromptResult.warning) {
				const warningMessage = structuredPromptResult.warning.toLowerCase();
				if (warningMessage.includes("intellectual property") || warningMessage.includes("ip-protected")) {
					textToImageStore.errors.generate = {
						message: "promptIpWarning",
						status: 200,
						isWarning: true,
					};
				}
			}
		});

		return structuredPromptResult;
	};

	generateStructuredPrompt = async (
		textToImageConfig?: TextToImageConfigType,
		isRefine?: boolean,
		refineNonFiboImage?: boolean,
		image?: PlaygroundImage,
	): Promise<any> => {
		const { textToImageStore } = this.rootStore;
		const config = textToImageConfig || textToImageStore.config;

		runInAction(() => {
			textToImageStore.errors = { ...textToImageStore.errors, generate: undefined };
		});
		this.abortController = new AbortController();

		try {
			runInAction(() => {
				this.isGeneratingStructuredPrompt = true;
				textToImageStore.IpSignalWarning = false;
			});

			if (!config.prompt && !textToImageStore.imageReference?.file && !isRefine) {
				return;
			}

			// Build base payload
			const payload = this.buildStructuredPromptPayload(
				config,
				isRefine || false,
				refineNonFiboImage || false,
				image,
			);

			// Only add refine-specific fields when isRefine is true
			const { payload: updatedPayload, isStructuredPromptModified } = isRefine
				? this.addRefineStructuredPromptToPayload(payload)
				: { payload, isStructuredPromptModified: false };

			// Determine endpoint
			const endpoint =
				isStructuredPromptModified && (isFiboBbqApp() || isFibo2App())
					? "/generate_structured_prompt_diff"
					: "/generate_structured_prompt";

			// Make API call
			let response = await this.makeStructuredPromptApiCall(
				endpoint,
				updatedPayload,
				config,
				isStructuredPromptModified,
				image,
				isRefine,
			);

			return this.updateStoreWithStructuredPromptResponse(response, isRefine || false);
		} catch (err: any) {
			runInAction(() => {
				this.isGeneratingStructuredPrompt = false;
				this.handleContentModerationError(err);
			});
			this.abortImageGeneration();
			return Promise.reject(err);
		}
	};

	private initializeGeneration = (functionOptions?: GenerateTextToImageV2Object): GenerationContext => {
		const { textToImageConfig, isRefine, isTgEvaluation, skipGenerateStructuredPrompt, image } =
			functionOptions || {};
		const { textToImageStore, playgroundStore } = this.rootStore;
		const config = textToImageConfig || textToImageStore.config;

		runInAction(() => {
			textToImageStore.errors = { ...textToImageStore.errors, generate: undefined };
		});

		this.abortController = new AbortController();
		const isTg = playgroundStore.selectedConfig === APPS.TG_IMAGE_GENERATION;
		const modelName = config.model_name || `${config.model} ${config.model_version}`;
		const savedIndex = playgroundStore.playgroundResults.length;
		const numResults = config.num_results || 1;

		const capturedImageReferenceUrl = textToImageStore.imageReference?.url || undefined;
		const capturedImageReferenceFile = textToImageStore.imageReference?.file;

		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config: { ...config, is_refine: isRefine },
			type: isTg ? APPS.TG_IMAGE_GENERATION : isFiboApp() ? LABS.FIBO : APPS.TEXT_TO_IMAGE,
			images: Array.from({ length: numResults }).map(() => ({
				id: uuidv4(),
				url: "",
				seed: NaN,
				loading: true,
				playground_result_id: "",
				aspect_ratio: config.aspect_ratio,
				ref_image_url: capturedImageReferenceUrl,
			})),
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];

		return {
			config,
			isRefine,
			isTgEvaluation,
			skipGenerateStructuredPrompt,
			image,
			isTg,
			modelName,
			savedIndex,
			numResults,
			resultsSkeletons,
			capturedImageReferenceUrl,
			capturedImageReferenceFile,
		};
	};

	private generateStructuredPromptIfNeeded = async (
		config: TextToImageConfigType,
		isRefine: boolean | undefined,
		image: PlaygroundImage | undefined,
	): Promise<{ seed: number | undefined; structured_prompt: string | undefined }> => {
		if (image?.refine_prompt && isRefine) {
			const { prompt, structured_prompt: _ignoredStructuredPrompt, ...rest } = config;

			const updatedConfig = {
				...rest,
				images: [image.url],
				seed: image.seed,
				steps_num:
					IMAGE_REFERENCE_CONFIGS.steps[
						config.model_id as unknown as keyof typeof IMAGE_REFERENCE_CONFIGS.steps
					].default,
			};
			return await this.generateStructuredPrompt(updatedConfig, isRefine, true, image);
		} else {
			return await this.generateStructuredPrompt(config, isRefine);
		}
	};

	private prepareImageGenerationPayload = async (params: ImageGenerationParams): Promise<ImageGenerationPayload> => {
		const {
			config,
			structured_prompt,
			seed,
			image,
			isRefine,
			skipGenerateStructuredPrompt,
			modelName,
			numResults,
			capturedImageReferenceFile,
		} = params;

		const payload = buildGenerationPayload({
			config,
			structured_prompt,
			seed,
			image,
			isRefine,
			skipGenerateStructuredPrompt,
		});
		const randomSeed = Math.floor(1000000000 + Math.random() * 1000000000);

		if (seed !== undefined && seed !== null) {
			payload.seed = parseInt(seed.toString());
		}

		postHogService.trackEvent(
			POSTHOG_EVENTS.IMAGE_GENERATION_STARTED,
			{
				model: modelName,
				prompt: payload.prompt,
				version: "v2",
				num_results: numResults,
			},
			{ send_instantly: true },
		);

		// Upload reference image if present (use captured file to avoid changes during polling)
		const formData = new FormData();
		capturedImageReferenceFile && formData.append("file", capturedImageReferenceFile);
		const uploadingPath = "/s3/temp_file";
		const refImageUrl = await this.fileQueryService.post(uploadingPath, formData, {
			"Content-Type": "multipart/form-data",
		});

		return { payload, refImageUrl, randomSeed };
	};

	private generateSingleImage = async (params: SingleImageGenerationParams): Promise<PlaygroundImage | null> => {
		const {
			imageIndex,
			payload,
			config,
			isRefine,
			isTg,
			isTgEvaluation,
			savedIndex,
			seed,
			skipGenerateStructuredPrompt,
			image,
			randomSeed,
			refImageUrl,
			capturedImageReferenceFile,
		} = params;
		const { playgroundStore } = this.rootStore;

		try {
			const imagePayload = { ...payload };
			if (seed !== undefined && seed !== null) {
				imagePayload.seed = parseInt(seed.toString()) + imageIndex;
			}
			if (skipGenerateStructuredPrompt) {
				const baseSeed = image?.seed
					? image?.seed
					: config.seed
						? parseInt(config.seed.toString())
						: randomSeed;
				const newSeed = baseSeed + imageIndex;
				imagePayload.seed = newSeed;
			}

			let initialResponse: any;
			if (capturedImageReferenceFile && skipGenerateStructuredPrompt && !(image?.refine_prompt && isRefine)) {
				const formData = new FormData();
				formData.append("file", capturedImageReferenceFile);
				formData.append(
					"config",
					JSON.stringify({
						...imagePayload,
						visual_input_content_moderation: !!config.content_moderation,
					}),
				);

				initialResponse = await this.imageGenerationsQueryService.post(
					"/image_generate",
					formData,
					{ "Content-Type": "multipart/form-data" },
					{ signal: this.abortController.signal },
				);
			} else {
				initialResponse = await this.imageGenerationsQueryService.post(
					"/image_generate",
					imagePayload,
					{ "Content-Type": "application/json" },
					{ signal: this.abortController.signal },
				);
			}

			if (initialResponse?.status_url && initialResponse?.request_id) {
				const currentResult = playgroundStore.playgroundResults[savedIndex];
				const currentImageEntry = currentResult?.images?.[imageIndex];
				if (!currentResult || !currentImageEntry) {
					return null;
				}

				const placeholderImage: PlaygroundImage = {
					id: currentImageEntry.id,
					url: "",
					loading: true,
					playground_result_id: "",
					config: { ...config, is_refine: isRefine },
					type: isTg ? APPS.TG_IMAGE_GENERATION : APPS.TEXT_TO_IMAGE,
					aspect_ratio: config.aspect_ratio,
					ui_type: isTgEvaluation ? "Tailored_gen_evaluation_interface" : undefined,
					status_url: initialResponse.status_url,
					ref_image_url: refImageUrl.length === 1 ? refImageUrl[0] : currentImageEntry.ref_image_url,
				};

				runInAction(() => {
					const result = playgroundStore.playgroundResults[savedIndex];
					if (result?.images?.[imageIndex]) {
						result.images[imageIndex] = placeholderImage;
					}
				});

				return placeholderImage;
			} else {
				throw new Error("Expected async response with status_url but got different response");
			}
		} catch (error: any) {
			const moderationInfo = this.detectContentModerationError(error);
			runInAction(() => {
				const currentResult = playgroundStore.playgroundResults[savedIndex];
				const currentImage = currentResult?.images?.[imageIndex];

				if (currentResult && currentImage) {
					currentResult.images[imageIndex] = {
						...currentImage,
						loading: false,
						error: moderationInfo?.isContentModeration
							? {
									message: "Content moderation error",
									status: moderationInfo.status,
									type: "contentModeration",
								}
							: {
									message: "Failed to generate image",
									status: 500,
								},
					};
				}
			});

			throw error;
		}
	};

	private processGenerationResults = (params: {
		results: PromiseSettledResult<PlaygroundImage | null>[];
		savedIndex: number;
		isRefine: boolean | undefined;
		structured_prompt: string | undefined;
		seed: number | undefined;
	}): void => {
		const { results, savedIndex, isRefine, structured_prompt, seed } = params;
		const { playgroundStore } = this.rootStore;

		const currentResult = playgroundStore.playgroundResults[savedIndex];
		if (currentResult) {
			(currentResult.config as TextToImageConfigType).is_refine = isRefine;

			if (structured_prompt) {
				(currentResult.config as TextToImageConfigType).structured_prompt = structured_prompt;
			}

			if (isRefine) {
				const selectedGroup = this.getSelectedFiboGroup();
				if (selectedGroup?.structured_prompt_diff) {
					(currentResult.config as TextToImageConfigType).structured_prompt_diff =
						selectedGroup.structured_prompt_diff;
				}
			}

			if (seed !== undefined && seed !== null) {
				(currentResult.config as TextToImageConfigType).seed = seed;
			}
		}

		const successfulImages = results.filter((result) => result.status === "fulfilled");
		const failedImages = results.filter((result) => result.status === "rejected");

		if (successfulImages.length === 0 && failedImages.length > 0) {
			const firstError = (failedImages[0] as PromiseRejectedResult).reason;
			throw firstError;
		}

		const usageLimitError = failedImages.find((result) => {
			const reason = (result as PromiseRejectedResult).reason;
			return reason?.response?.status === 429 && reason?.response?.data?.code === "ANONYMOUS_USAGE_LIMIT_REACHED";
		});
		if (usageLimitError) {
			throw (usageLimitError as PromiseRejectedResult).reason;
		}
	};

	/**
	 * Handles errors for the entire image generation batch.
	 * Tracks the failure event, updates global state, handles content moderation errors,
	 * and removes the failed result from the store.
	 */
	private handleGenerationError = (params: { err: any; modelName: string; savedIndex: number }): void => {
		const { err, modelName, savedIndex } = params;
		const { textToImageStore, playgroundStore } = this.rootStore;

		postHogService.trackEvent(
			POSTHOG_EVENTS.IMAGE_GENERATION_FAILED,
			{
				model: modelName,
				error: err.response?.data?.message || err.message || "Unknown error",
				error_status: err.response?.status,
				version: "v2",
			},
			{ send_instantly: true },
		);

		runInAction(() => {
			textToImageStore.isGeneratingImages = false;
			this.lastGeneratedResultId = null;

			this.handleContentModerationError(err, {
				handleImageReferenceError: false,
				promptErrorMessage: "textToImagePlaygroundPromptContentModeration",
			});

			playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
				(_, index) => index !== savedIndex,
			);
		});
	};

	/**
	 * FIBO 2 generate/refine flow.
	 *
	 * Unlike v1/BBQ (structured prompt FIRST, then image), FIBO 2 renders the image first
	 * and derives the vgl (ImageDescription) from it:
	 *   1) POST /image_generate_fibo2  -> { image.url, seed, vgl }   (image + seed shown first)
	 *   2) POST /generate_vgl_fibo2    -> { vgl }                    (shown as the VGL Output)
	 *
	 * Refine differs only in that phase 1 also passes the edited vgl of the selected result,
	 * so the new image is rendered from that caption (vgl passthrough). No diff endpoint.
	 */
	private generateFibo2 = async (context: GenerationContext): Promise<void> => {
		const { textToImageStore, playgroundStore } = this.rootStore;
		const { config, isRefine, savedIndex, modelName } = context;

		const resultId = playgroundStore.playgroundResults[savedIndex]?.id;
		// Capture the edited caption of the currently-selected (source) result for refine,
		// before selection moves to the freshly-created result.
		const sourceVgl = isRefine
			? (this.getSelectedFiboGroup()?.structured_prompt ?? config.structured_prompt)
			: undefined;

		try {
			// ----- Phase 1: image (shown first) -----
			runInAction(() => {
				textToImageStore.isGeneratingImages = true;
				textToImageStore.IpSignalWarning = false;
			});

			let imageBody: any;

			if (this.inputMode === "json") {
				// JSON mode: use the raw JSON from the editor as the request body.
				// Drop the `vgl` if it's still the empty placeholder so it stays optional.
				try {
					const parsed = JSON.parse(this.jsonInput);
					if (this.isEmptyVgl(parsed?.vgl)) delete parsed.vgl;
					imageBody = { ...parsed, sync: true };
				} catch {
					imageBody = { sync: true };
				}
			} else {
				// Form mode: build payload from individual config fields
				imageBody = {
					aspect_ratio: config.aspect_ratio,
					steps_num: config.steps_num,
					sync: true,
				};
				if (config.prompt) imageBody.prompt = config.prompt;
				if (config.negative_prompt) imageBody.negative_prompt = config.negative_prompt;
				if (sourceVgl) imageBody.vgl = sourceVgl; // refine: render from the edited caption
				if (config.seed !== undefined && config.seed !== null) {
					imageBody.seed = parseInt(config.seed.toString());
				}
			}

			const imageResponse = await this.imageGenerationsQueryService.post(
				"/image_generate_fibo2",
				imageBody,
				{ "Content-Type": "application/json" },
				{ signal: this.abortController.signal },
			);

			const imageUrl: string = imageResponse?.image?.url ?? "";
			const imageSeed: number | undefined = imageResponse?.seed;

			runInAction(() => {
				const result = playgroundStore.playgroundResults[savedIndex];
				const entry = result?.images?.[0];
				if (result && entry) {
					result.images[0] = {
						id: entry.id,
						url: imageUrl,
						seed: imageSeed ?? NaN,
						loading: false,
						playground_result_id: "",
						config: { ...config, is_refine: isRefine, model_version: "FIBO_2" },
						aspect_ratio: config.aspect_ratio,
					};
					(result.config as TextToImageConfigType).is_refine = isRefine;
					if (imageSeed !== undefined && imageSeed !== null) {
						(result.config as TextToImageConfigType).seed = imageSeed;
					}
				}
				textToImageStore.isGeneratingImages = false;
			});

			// ----- Phase 2: vgl (caption the generated image into its full vgl) -----
			// The VGL Output comes ONLY from /generate_vgl_fibo2 (never from the image call —
			// see the class contract). If it fails we keep the image but leave the VGL Output
			// empty (no fallback). Decided in BLD-211: the VGL Output is the description of
			// the image you are looking at, not the payload that produced it. The trade-off
			// is that the first Refine starts from the caption rather than the expansion, so
			// its fidelity to the original image is bounded by how well caption -> render
			// round-trips.
			runInAction(() => {
				this.isGeneratingStructuredPrompt = true;
			});

			let finalVgl: string | undefined;
			if (imageUrl) {
				try {
					const vglBody: any = { image_url: imageUrl };
					if (imageSeed !== undefined && imageSeed !== null) vglBody.seed = imageSeed;

					const vglResponse = await this.imageGenerationsQueryService.post(
						"/generate_vgl_fibo2",
						vglBody,
						{ "Content-Type": "application/json" },
						{ signal: this.abortController.signal },
					);

					finalVgl = vglResponse?.vgl;
				} catch (vglErr) {
					// Keep the generated image; do not fail the whole generation over a vgl error.
					console.error("generate_vgl_fibo2 failed", vglErr);
				}
			}

			runInAction(() => {
				this.isGeneratingStructuredPrompt = false;
				// Internally we keep the `structured_prompt` field name (shared model); the
				// fibo2 UI labels it "VGL Output".
				if (resultId && finalVgl) {
					this.updateFiboResultGroup(resultId, {
						structured_prompt: finalVgl,
						original_structured_prompt: finalVgl,
					});
				}
				this.lastGeneratedResultId = resultId ?? null;
			});
		} catch (err: any) {
			runInAction(() => {
				this.isGeneratingStructuredPrompt = false;
			});
			this.handleGenerationError({ err, modelName, savedIndex });
			return Promise.reject(err);
		}
	};

	/**
	 * FIBO 2 inspire flow (triggered when the user provides an input image on the generate tab).
	 *   1) Upload the input image to get a hosted URL.
	 *   2) POST /generate_vgl_fibo2 { image_url } -> vgl (caption the input image; the VGL Output).
	 *   3) POST /image_generate_fibo2 { vgl }     -> image (rendered from that vgl).
	 * The caption from step 2 stays the VGL Output — the render response's inline vgl is
	 * never used (class contract). Note the caption describes the INPUT image; the render
	 * may drift from it.
	 */
	private inspireFibo2 = async (context: GenerationContext): Promise<void> => {
		const { textToImageStore, playgroundStore } = this.rootStore;
		const { config, savedIndex, modelName, capturedImageReferenceFile, capturedImageReferenceUrl } = context;
		const resultId = playgroundStore.playgroundResults[savedIndex]?.id;
		const seedNum =
			config.seed !== undefined && config.seed !== null ? parseInt(config.seed.toString()) : undefined;

		try {
			// ----- Step 1: upload the input image to get a hosted URL -----
			let inputImageUrl = capturedImageReferenceUrl;
			if (capturedImageReferenceFile) {
				const formData = new FormData();
				formData.append("file", capturedImageReferenceFile);
				const uploaded = await this.fileQueryService.post(
					"/s3/temp_file",
					formData,
					{ "Content-Type": "multipart/form-data" },
					{ signal: this.abortController.signal },
				);
				inputImageUrl = Array.isArray(uploaded) ? uploaded[0] : uploaded;
			}

			if (!inputImageUrl) {
				this.handleGenerationError({
					err: new Error("Inspire requires an input image"),
					modelName,
					savedIndex,
				});
				return;
			}

			// ----- Step 2: caption the input image into a vgl -----
			runInAction(() => {
				this.isGeneratingStructuredPrompt = true;
				textToImageStore.IpSignalWarning = false;
			});

			const vglBody: any = { image_url: inputImageUrl };
			if (seedNum !== undefined) vglBody.seed = seedNum;

			const vglResponse = await this.imageGenerationsQueryService.post(
				"/generate_vgl_fibo2",
				vglBody,
				{ "Content-Type": "application/json" },
				{ signal: this.abortController.signal },
			);
			const vgl: string | undefined = vglResponse?.vgl;

			runInAction(() => {
				this.isGeneratingStructuredPrompt = false;
				if (resultId && vgl) {
					this.updateFiboResultGroup(resultId, {
						structured_prompt: vgl,
						original_structured_prompt: vgl,
					});
				}
			});

			if (!vgl) {
				this.handleGenerationError({
					err: new Error("Failed to generate VGL from the input image"),
					modelName,
					savedIndex,
				});
				return;
			}

			// ----- Step 3: render an image from the vgl -----
			runInAction(() => {
				textToImageStore.isGeneratingImages = true;
			});

			const imageBody: any = {
				vgl,
				aspect_ratio: config.aspect_ratio,
				steps_num: config.steps_num,
				sync: true,
			};
			if (config.negative_prompt) imageBody.negative_prompt = config.negative_prompt;
			if (seedNum !== undefined) imageBody.seed = seedNum;

			const imageResponse = await this.imageGenerationsQueryService.post(
				"/image_generate_fibo2",
				imageBody,
				{ "Content-Type": "application/json" },
				{ signal: this.abortController.signal },
			);

			const imageUrl: string = imageResponse?.image?.url ?? "";
			const imageSeed: number | undefined = imageResponse?.seed;

			runInAction(() => {
				const result = playgroundStore.playgroundResults[savedIndex];
				const entry = result?.images?.[0];
				if (result && entry) {
					result.images[0] = {
						id: entry.id,
						url: imageUrl,
						seed: imageSeed ?? NaN,
						loading: false,
						playground_result_id: "",
						config: { ...config, model_version: "FIBO_2" },
						aspect_ratio: config.aspect_ratio,
						ref_image_url: inputImageUrl, // marks this as an "inspired" result in the UI
					};
					if (imageSeed !== undefined && imageSeed !== null) {
						(result.config as TextToImageConfigType).seed = imageSeed;
					}
				}
				textToImageStore.isGeneratingImages = false;
				this.lastGeneratedResultId = resultId ?? null;
			});
		} catch (err: any) {
			runInAction(() => {
				this.isGeneratingStructuredPrompt = false;
			});
			this.handleGenerationError({ err, modelName, savedIndex });
			return Promise.reject(err);
		}
	};

	/**
	 * FIBO 2 refine flow.
	 *
	 * The fal `refine` model applies a natural-language edit to an existing vgl and
	 * returns a refined vgl — it does NOT render. So refine is two steps:
	 *   1) POST /refine_vgl_fibo2  { vgl, edit_instruction } -> refined vgl (shown while rendering)
	 *   2) POST /image_generate_fibo2 { vgl: refined, seed } -> image (rendered from the refined vgl)
	 * The refined vgl from step 1 IS the VGL Output — final. There is no re-caption step
	 * after the render, and the render response's inline vgl is never used (see the
	 * class contract above).
	 *
	 * Structural/text VGL edits made with NO typed instruction (canvas/tree edits only)
	 * skip step 1 entirely — the edited vgl is rendered as-is rather than being reworded
	 * by the refine model or re-captioned from the pixels, either of which would discard
	 * the user's own edits. Requires either a typed instruction or an existing/edited
	 * vgl on the selected result (button-gated too).
	 *
	 * The VGL Output is in its loading state for the entire refine (Lev, 2026-08-17):
	 * the panel participates in the lifecycle like the image does and cannot be edited
	 * mid-flight. The vgl is still committed to the group as soon as it is known, so
	 * the state a viewer lands on (e.g. switching experiments) is always correct.
	 */
	private refineFibo2 = async (context: GenerationContext): Promise<void> => {
		const { textToImageStore, playgroundStore } = this.rootStore;
		const { config, savedIndex, modelName } = context;

		const resultId = playgroundStore.playgroundResults[savedIndex]?.id;
		const sourceGroup = this.getSelectedFiboGroup();
		const sourceSeed = sourceGroup?.seed ?? config.seed;

		let sourceVgl: string | undefined;
		let instruction: string | undefined;

		if (this.inputMode === "json") {
			try {
				const parsed = JSON.parse(this.jsonInput);
				instruction = parsed?.edit_instruction;
				const jsonVgl = parsed?.vgl;
				sourceVgl = this.isEmptyVgl(jsonVgl)
					? (sourceGroup?.structured_prompt ?? config.structured_prompt)
					: typeof jsonVgl === "string"
						? jsonVgl
						: JSON.stringify(jsonVgl);
			} catch {
				sourceVgl = sourceGroup?.structured_prompt ?? config.structured_prompt;
				instruction = config.prompt;
			}
		} else {
			sourceVgl = sourceGroup?.structured_prompt ?? config.structured_prompt;
			instruction = config.prompt;
		}

		if (!sourceVgl) {
			this.handleGenerationError({
				err: new Error("Refine requires an existing VGL"),
				modelName,
				savedIndex,
			});
			return;
		}

		// Natural-language edit -> /refine (LLM applies the instruction to the vgl).
		// Structural/text edits only (no instruction) -> send the edited vgl straight to
		// generate; the model renders it as-is (no refine, no edit_instruction).
		const hasInstruction = !!(instruction && instruction.trim());

		try {
			let vglToRender: string = sourceVgl;

			// The VGL Output stays in its loading state for the WHOLE refine — through the
			// render, not just the /refine call — so the panel participates in the refine
			// lifecycle the way the image does, and cannot be edited mid-flight. The vgl is
			// still committed below so the group's state is correct throughout.
			runInAction(() => {
				this.isGeneratingStructuredPrompt = true;
				textToImageStore.IpSignalWarning = false;
			});

			if (hasInstruction) {
				// ----- Phase 1: refine the vgl via the instruction (no render) -----
				const refineResponse = await this.imageGenerationsQueryService.post(
					"/refine_vgl_fibo2",
					{ vgl: sourceVgl, edit_instruction: instruction },
					{ "Content-Type": "application/json" },
					{ signal: this.abortController.signal },
				);
				const refinedVgl: string | undefined = refineResponse?.vgl;

				runInAction(() => {
					if (resultId && refinedVgl) {
						this.updateFiboResultGroup(resultId, {
							structured_prompt: refinedVgl,
							original_structured_prompt: refinedVgl,
						});
					}
				});

				if (!refinedVgl) {
					runInAction(() => {
						this.isGeneratingStructuredPrompt = false;
						this.lastGeneratedResultId = resultId ?? null;
					});
					return;
				}
				vglToRender = refinedVgl;
			} else {
				// ----- Structural/text edits only: apply the edited vgl directly -----
				runInAction(() => {
					if (resultId) {
						this.updateFiboResultGroup(resultId, {
							structured_prompt: sourceVgl,
							original_structured_prompt: sourceVgl,
						});
					}
				});
			}

			// ----- Render the vgl (edited or refined) -----
			runInAction(() => {
				textToImageStore.isGeneratingImages = true;
			});

			const imageBody: any = {
				vgl: vglToRender,
				aspect_ratio: config.aspect_ratio,
				steps_num: config.steps_num,
				sync: true,
			};
			if (sourceSeed !== undefined && sourceSeed !== null) {
				imageBody.seed = parseInt(sourceSeed.toString());
			}

			const imageResponse = await this.imageGenerationsQueryService.post(
				"/image_generate_fibo2",
				imageBody,
				{ "Content-Type": "application/json" },
				{ signal: this.abortController.signal },
			);

			const imageUrl: string = imageResponse?.image?.url ?? "";
			const imageSeed: number | undefined = imageResponse?.seed;

			runInAction(() => {
				const result = playgroundStore.playgroundResults[savedIndex];
				const entry = result?.images?.[0];
				if (result && entry) {
					result.images[0] = {
						id: entry.id,
						url: imageUrl,
						seed: imageSeed ?? NaN,
						loading: false,
						playground_result_id: "",
						config: { ...config, is_refine: true, model_version: "FIBO_2" },
						aspect_ratio: config.aspect_ratio,
					};
					(result.config as TextToImageConfigType).is_refine = true;
					if (imageSeed !== undefined && imageSeed !== null) {
						(result.config as TextToImageConfigType).seed = imageSeed;
					}
				}
				this.isGeneratingStructuredPrompt = false;
				textToImageStore.isGeneratingImages = false;
				this.lastGeneratedResultId = resultId ?? null;
			});
		} catch (err: any) {
			runInAction(() => {
				this.isGeneratingStructuredPrompt = false;
			});
			this.handleGenerationError({ err, modelName, savedIndex });
			return Promise.reject(err);
		}
	};

	/**
	 * FIBO 2 "embed image" flow, triggered by Refine when the user has uploaded an image
	 * to embed into the current scene.
	 *   1) Upload the image to get a hosted URL.
	 *   2) POST /embed_object_fibo2 { vgl, object_image_url, target_bbox } -> the current
	 *      scene re-rendered with that image embedded at `target_bbox`.
	 *
	 * `target_bbox` is simply the embed area on the canvas. Drawing it freehand and seeding
	 * it from a detected object's box produce the same rectangle, so there is one payload
	 * shape and one code path — and embedding into empty space, across several objects, or
	 * into a scene with nothing detected all fall out for free.
	 */
	private embedImageFibo2 = async (context: GenerationContext, file: File, bbox: BoundingBox): Promise<void> => {
		const { textToImageStore, playgroundStore } = this.rootStore;
		const { config, savedIndex, modelName } = context;

		const resultId = playgroundStore.playgroundResults[savedIndex]?.id;
		const sourceGroup = this.getSelectedFiboGroup();
		const sourceVgl = sourceGroup?.structured_prompt ?? config.structured_prompt;
		const sourceSeed = sourceGroup?.seed ?? config.seed;

		try {
			runInAction(() => {
				textToImageStore.isGeneratingImages = true;
				// The VGL Output loads for the whole flow, same lifecycle as refine. The vgl
				// itself stays the pre-embed scene (the embed response's inline vgl is never
				// used — class contract), so it does NOT describe the embedded object yet;
				// whether to re-caption the result is an open product decision.
				this.isGeneratingStructuredPrompt = true;
				textToImageStore.IpSignalWarning = false;
			});

			// ----- Step 1: upload the replacement image to get a hosted URL -----
			const formData = new FormData();
			formData.append("file", file);
			const uploaded = await this.fileQueryService.post(
				"/s3/temp_file",
				formData,
				{ "Content-Type": "multipart/form-data" },
				{ signal: this.abortController.signal },
			);
			const objectImageUrl: string = Array.isArray(uploaded) ? uploaded[0] : uploaded;

			// ----- Step 2: embed the object into the scene -----
			const imageBody: any = {
				object_image_url: objectImageUrl,
				target_bbox: [bbox.left, bbox.top, bbox.left + bbox.width, bbox.top + bbox.height],
				aspect_ratio: config.aspect_ratio,
				steps_num: config.steps_num,
				sync: true,
			};
			if (sourceVgl) imageBody.vgl = sourceVgl;
			if (config.negative_prompt) imageBody.negative_prompt = config.negative_prompt;
			if (sourceSeed !== undefined && sourceSeed !== null) {
				imageBody.seed = parseInt(sourceSeed.toString());
			}

			const imageResponse = await this.imageGenerationsQueryService.post(
				"/embed_object_fibo2",
				imageBody,
				{ "Content-Type": "application/json" },
				{ signal: this.abortController.signal },
			);

			const imageUrl: string = imageResponse?.image?.url ?? "";
			const imageSeed: number | undefined = imageResponse?.seed;

			runInAction(() => {
				const result = playgroundStore.playgroundResults[savedIndex];
				const entry = result?.images?.[0];
				if (result && entry) {
					result.images[0] = {
						id: entry.id,
						url: imageUrl,
						seed: imageSeed ?? NaN,
						loading: false,
						playground_result_id: "",
						config: { ...config, is_refine: true, model_version: "FIBO_2" },
						aspect_ratio: config.aspect_ratio,
					};
					(result.config as TextToImageConfigType).is_refine = true;
					if (imageSeed !== undefined && imageSeed !== null) {
						(result.config as TextToImageConfigType).seed = imageSeed;
					}
				}
				this.isGeneratingStructuredPrompt = false;
				textToImageStore.isGeneratingImages = false;
				this.lastGeneratedResultId = resultId ?? null;
				// Consumed — the area and its image go together, so the next Refine doesn't
				// re-embed into the same place.
				this.structuredPromptStore.clearEmbedDraw();
			});
		} catch (err: any) {
			runInAction(() => {
				this.isGeneratingStructuredPrompt = false;
				textToImageStore.isGeneratingImages = false;
			});
			this.handleGenerationError({ err, modelName, savedIndex });
			return Promise.reject(err);
		}
	};

	generate = async (functionOptions?: GenerateTextToImageV2Object): Promise<void> => {
		// Commit any pending debounced VGL edits (canvas bbox/color/keypoint, tree fields)
		// so the payload reflects the latest modification.
		this.flushVglEdits();

		// Ensure isRefine is always false/undefined for generate
		const optionsWithoutRefine = { ...functionOptions };
		if (optionsWithoutRefine.isRefine !== undefined) {
			delete optionsWithoutRefine.isRefine;
		}

		const context = this.initializeGeneration(optionsWithoutRefine);

		// FIBO 2: inverted flow (image first, then vgl).

		if (isFibo2App()) {
			// Inspire: an input image was provided -> caption it, then render from that vgl.
			// Plain generate: prompt only -> render, then caption.
			if (context.capturedImageReferenceFile || context.capturedImageReferenceUrl) {
				return this.inspireFibo2(context);
			}
			return this.generateFibo2(context);
		}
		let seed: number | undefined;
		let structured_prompt: string | undefined;

		if (!context.skipGenerateStructuredPrompt) {
			({ seed, structured_prompt } = await this.generateStructuredPromptIfNeeded(
				context.config,
				context.isRefine,
				context.image,
			));
		}

		try {
			const { textToImageStore } = this.rootStore;
			runInAction(() => {
				textToImageStore.isGeneratingImages = true;
				textToImageStore.IpSignalWarning = false;
			});

			const { payload, refImageUrl, randomSeed } = await this.prepareImageGenerationPayload({
				config: context.config,
				structured_prompt,
				seed,
				image: context.image,
				isRefine: context.isRefine,
				skipGenerateStructuredPrompt: context.skipGenerateStructuredPrompt,
				modelName: context.modelName,
				numResults: context.numResults,
				capturedImageReferenceFile: context.capturedImageReferenceFile,
			});

			const singleImageParams: Omit<SingleImageGenerationParams, "imageIndex"> = {
				payload,
				config: context.config,
				isRefine: context.isRefine,
				isTg: context.isTg,
				isTgEvaluation: context.isTgEvaluation,
				savedIndex: context.savedIndex,
				seed,
				skipGenerateStructuredPrompt: context.skipGenerateStructuredPrompt,
				image: context.image,
				randomSeed,
				refImageUrl,
				capturedImageReferenceFile: context.capturedImageReferenceFile,
			};

			const imagePromises = Array.from({ length: context.numResults }).map((_, index) =>
				this.generateSingleImage({ ...singleImageParams, imageIndex: index }),
			);
			const results = await Promise.allSettled(imagePromises);

			this.processGenerationResults({
				results,
				savedIndex: context.savedIndex,
				isRefine: context.isRefine,
				structured_prompt,
				seed,
			});

			runInAction(() => {
				const { textToImageStore, playgroundStore } = this.rootStore;
				const currentResult = playgroundStore.playgroundResults[context.savedIndex];
				textToImageStore.isGeneratingImages = false;
				this.lastGeneratedResultId = currentResult?.id || null;
			});
		} catch (err: any) {
			this.handleGenerationError({
				err,
				modelName: context.modelName,
				savedIndex: context.savedIndex,
			});
			return Promise.reject(err);
		}
	};

	refine = async (functionOptions?: Omit<GenerateTextToImageV2Object, "isRefine">): Promise<void> => {
		// Commit any pending debounced VGL edits so refine sends the latest modification.
		this.flushVglEdits();

		// Always set isRefine to true for refine flow
		const context = this.initializeGeneration({ ...functionOptions, isRefine: true });

		// FIBO 2: an embed area holding an image takes precedence — composite it into the
		// scene instead of running the normal vgl-edit refine. Both halves are required;
		// the Refine button is gated on the same condition.
		const { embedFile, embedBbox } = this.structuredPromptStore;
		if (isFibo2App() && embedFile && embedBbox) {
			return this.embedImageFibo2(context, embedFile, embedBbox);
		}

		// FIBO 2: refine = edit the vgl (refine model) then render it (no re-caption).
		if (isFibo2App()) {
			return this.refineFibo2(context);
		}

		let seed: number | undefined;
		let structured_prompt: string | undefined;

		if (!context.skipGenerateStructuredPrompt) {
			({ seed, structured_prompt } = await this.generateStructuredPromptIfNeeded(
				context.config,
				context.isRefine,
				context.image,
			));
		}

		try {
			const { textToImageStore } = this.rootStore;
			runInAction(() => {
				textToImageStore.isGeneratingImages = true;
				textToImageStore.IpSignalWarning = false;
			});

			const { payload, refImageUrl, randomSeed } = await this.prepareImageGenerationPayload({
				config: context.config,
				structured_prompt,
				seed,
				image: context.image,
				isRefine: context.isRefine,
				skipGenerateStructuredPrompt: context.skipGenerateStructuredPrompt,
				modelName: context.modelName,
				numResults: context.numResults,
				capturedImageReferenceFile: context.capturedImageReferenceFile,
			});

			const singleImageParams: Omit<SingleImageGenerationParams, "imageIndex"> = {
				payload,
				config: context.config,
				isRefine: context.isRefine,
				isTg: context.isTg,
				isTgEvaluation: context.isTgEvaluation,
				savedIndex: context.savedIndex,
				seed,
				skipGenerateStructuredPrompt: context.skipGenerateStructuredPrompt,
				image: context.image,
				randomSeed,
				refImageUrl,
				capturedImageReferenceFile: context.capturedImageReferenceFile,
			};

			const imagePromises = Array.from({ length: context.numResults }).map((_, index) =>
				this.generateSingleImage({ ...singleImageParams, imageIndex: index }),
			);
			const results = await Promise.allSettled(imagePromises);

			this.processGenerationResults({
				results,
				savedIndex: context.savedIndex,
				isRefine: context.isRefine,
				structured_prompt,
				seed,
			});

			runInAction(() => {
				const { textToImageStore, playgroundStore } = this.rootStore;
				const currentResult = playgroundStore.playgroundResults[context.savedIndex];
				textToImageStore.isGeneratingImages = false;
				this.lastGeneratedResultId = currentResult?.id || null;
			});
		} catch (err: any) {
			this.handleGenerationError({
				err,
				modelName: context.modelName,
				savedIndex: context.savedIndex,
			});
			return Promise.reject(err);
		}
	};
}

const buildGenerationPayload = ({
	config,
	structured_prompt,
	seed,
	image,
	isRefine,
	skipGenerateStructuredPrompt,
}: any) => {
	const base: any = {
		aspect_ratio: config.aspect_ratio,
		steps_num:
			image?.refine_prompt && !config.tailored_model_id
				? IMAGE_REFERENCE_CONFIGS.steps[
						config.model_id as unknown as keyof typeof IMAGE_REFERENCE_CONFIGS.steps
					].default
				: config.steps_num || 30,
		structured_prompt,
		visual_output_content_moderation: true,
		model_version: isFiboBbqApp() ? "FIBO_BBQ" : isFibo2App() ? "FIBO_2" : config.model_version,
		...(isRefine ? { is_refine: true } : {}),
		...(config.negative_prompt ? { negative_prompt: config.negative_prompt } : {}),
		...(config.enhance_image !== undefined && { enhance_image: config.enhance_image }),
		...(config.text_guidance_scale !== undefined && { guidance_scale: config.text_guidance_scale }),
	};

	const skipStructuredOrRefine = skipGenerateStructuredPrompt || (image?.refine_prompt && isRefine);
	if (skipStructuredOrRefine) {
		const prefix =
			config.medium === "art"
				? i18next.t("playground.textToImage.config.anIllustrationOf")
				: config.medium === "photography"
					? i18next.t("playground.textToImage.config.aPhotoOf")
					: "";

		const prompt = isRefine ? image?.refine_prompt : prefix ? `${prefix.trim()} ${config.prompt}` : config.prompt;

		return {
			...base,
			model: ["fibo", "fibo_lite"].includes(config.model) ? config.model : "fibo",
			structured_prompt: structured_prompt ?? (isRefine ? image?.structured_prompt : undefined),
			seed: seed !== undefined ? parseInt(seed.toString(), 10) : (image?.seed ?? config.seed),
			ip_signal: true,
			...(prompt ? { prompt } : {}),
			visual_output_content_moderation: !!config.content_moderation,
			prompt_content_moderation: !!config.content_moderation,
		};
	}

	return base;
};
